// Early-Abandon / RTP-Fork Detector — flags sessions that terminated so
// quickly the bot couldn't have had a conversation. These are the first
// candidates for the Cisco media-fork investigation (SR 701596879).
//
// WHY this report exists:
//   Prisma Ortho sees a long tail of sessions where the AI Agent session
//   row says the bot "greeted and then the caller abandoned" — <30s total,
//   fewer than 2 messages, often zero human turns. In practice these are
//   usually RTP-fork / early-leg-detach artefacts where the caller never
//   actually heard the bot's prompt because the media leg was still being
//   set up. Pure session-row scan — no deep fetch needed — so it's cheap
//   to run across the whole window and is the quickest way to size the
//   problem for a Cisco escalation.
//
// SIGNALS used (all from the contactAIAgentSession row — no message
// fetching):
//   * duration < 30_000ms           — total session time (ms)
//   * noOfTotalMessages < 2         — at most 1 message, so bot said one
//                                     thing (or nothing) and session ended
//
// A session matches if EITHER signal fires. We bucket by duration band
// (0-5s / 5-10s / 10-20s / 20-30s) so the shape of the distribution tells
// an engineer whether it's "callers ghost after 2s" vs "callers hang up at
// the end of the greeting" vs. something in between.
//
// GAPS:
//   * We can't tell from the session row alone whether the caller SPOKE at
//     all — `noOfTotalMessages < 2` is a proxy. Deep-fetch would get the
//     `userType` sequence; we're intentionally staying cheap here.
//   * `noOfTotalMessages` can be null (some session shapes). We treat null
//     the same as "<2" — assume the worst.
//   * agentId↔agentName — same gotcha as every other grouped report:
//     empty agentName with populated agentId. We resolve via a one-shot
//     list-ai-agents fetch.

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconInfoCircle } from '@tabler/icons-react';

import { ReportTable } from './ReportTable';
import { SessionListModal } from '../components/SessionListModal';
import { callTool } from '../../../api_scripts/wxccMcpClient';
import { fmtDuration } from '../utils';

const REPORT_ID = 'early-abandon';

// Thresholds. Tweakable only here to keep the report's definition in one
// place; if these move, the CSV + the banner language should move with them.
const EARLY_DURATION_MS = 30_000;
const EARLY_MESSAGE_FLOOR = 2;
const BUCKETS = [
  { id: 'd0_5',   label: '0-5s',   lo: 0,      hi: 5_000 },
  { id: 'd5_10',  label: '5-10s',  lo: 5_000,  hi: 10_000 },
  { id: 'd10_20', label: '10-20s', lo: 10_000, hi: 20_000 },
  { id: 'd20_30', label: '20-30s', lo: 20_000, hi: 30_000 },
];

const COLUMNS = [
  {
    id: 'bot',
    label: 'Bot',
    pinned: true,
    render: (r) => <Text size="sm" fw={500}>{r.bot.replace(/^PROD-/, '')}</Text>,
  },
  {
    id: 'earlyCount',
    label: '<30s count',
    pinned: true,
    align: 'right',
    hint: 'Sessions matching duration <30s OR <2 total messages. Click row to see the sessions.',
    render: (r) => (
      <Badge size="sm" color={r.earlyCount > 0 ? 'orange' : 'gray'} variant={r.earlyCount > 0 ? 'filled' : 'light'}>
        {r.earlyCount}
      </Badge>
    ),
  },
  ...BUCKETS.map((b) => ({
    id: b.id,
    label: b.label,
    align: 'right',
    hint: `Sessions with duration in [${b.lo / 1000}s, ${b.hi / 1000}s).`,
    render: (r) => <Text size="sm">{r.buckets[b.id] ?? 0}</Text>,
  })),
  {
    id: 'avgBotMsgs',
    label: 'Avg bot msgs',
    align: 'right',
    hint: 'Average noOfTotalMessages across the <30s / <2-msg sessions.',
    render: (r) => <Text size="sm">{r.avgMessages != null ? r.avgMessages.toFixed(1) : '—'}</Text>,
  },
  {
    id: 'avgDuration',
    label: 'Avg duration',
    align: 'right',
    hint: 'Average session duration across the <30s / <2-msg sessions.',
    render: (r) => <Text size="sm">{r.avgDurationMs != null ? fmtDuration(r.avgDurationMs) : '—'}</Text>,
  },
  {
    id: 'totalSessions',
    label: 'Total sessions',
    align: 'right',
    hint: 'All sessions for this bot in the window (for scale context).',
    render: (r) => <Text size="sm" c="dimmed">{r.totalSessions}</Text>,
  },
  {
    id: 'earlyRate',
    label: 'Early rate',
    align: 'right',
    hint: '<30s count ÷ total sessions for this bot.',
    render: (r) => {
      const rate = r.totalSessions ? Math.round((r.earlyCount / r.totalSessions) * 100) : 0;
      const color = rate >= 25 ? 'red' : rate >= 10 ? 'orange' : rate > 0 ? 'yellow' : 'gray';
      return <Badge size="sm" color={color} variant="light">{rate}%</Badge>;
    },
  },
];

const DEFAULT_VISIBLE = ['d0_5', 'd5_10', 'd10_20', 'd20_30', 'avgBotMsgs', 'avgDuration', 'totalSessions', 'earlyRate'];

export function EarlyAbandon({ data, onViewSession, window }) {
  const sessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);
  // agentId → canonical name — handles sessions that landed with an empty
  // agentName but a populated agentId. Same pattern as BusinessOutcomes.
  const [agentIdToName, setAgentIdToName] = useState({});

  useEffect(() => {
    let cancelled = false;
    callTool('ops', 'wxcc-operations-list-ai-agents', { view: 'list' })
      .then((res) => {
        if (cancelled) return;
        const reverse = {};
        for (const a of res?.agents ?? []) {
          if (a.name && a.id) reverse[a.id] = a.name;
        }
        setAgentIdToName(reverse);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const { rows, totalEarly, totalSessions, sessionsByBot } = useMemo(
    () => buildRows(sessions, agentIdToName),
    [sessions, agentIdToName],
  );

  const [drill, setDrill] = useState(null); // { bot, sessions }

  const csvRows = rows.map((r) => ({
    bot: r.bot,
    early_count: r.earlyCount,
    bucket_0_5s: r.buckets.d0_5 ?? 0,
    bucket_5_10s: r.buckets.d5_10 ?? 0,
    bucket_10_20s: r.buckets.d10_20 ?? 0,
    bucket_20_30s: r.buckets.d20_30 ?? 0,
    avg_messages: r.avgMessages != null ? r.avgMessages.toFixed(2) : '',
    avg_duration_ms: r.avgDurationMs != null ? Math.round(r.avgDurationMs) : '',
    total_sessions: r.totalSessions,
    early_rate_pct: r.totalSessions ? Math.round((r.earlyCount / r.totalSessions) * 100) : 0,
  }));

  return (
    <Stack gap="xs">
      <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
        <Text size="xs">
          These sessions are likely Cisco media-fork candidates (see SR 701596879). Each matches{' '}
          <strong>duration &lt; 30s</strong> OR <strong>fewer than 2 total messages</strong> — the bot
          barely had a chance to speak. Export the CSV and attach to the ticket.
        </Text>
      </Alert>

      <ReportTable
        title={`Early-abandon sessions (${totalEarly} of ${totalSessions} total)`}
        subtitle="Per-bot distribution of sessions that terminated inside 30 seconds or logged fewer than 2 messages. Pure session-row scan — no message fetches required."
        reportId={REPORT_ID}
        columns={COLUMNS}
        defaultVisibleIds={DEFAULT_VISIBLE}
        rows={rows}
        emptyLabel="No early-abandon sessions in this window."
        onRowClick={onViewSession ? (r) => setDrill({
          bot: r.bot,
          sessions: sessionsByBot.get(r.bot) ?? [],
        }) : undefined}
        csvExport={{
          filename: `early-abandon-${new Date().toISOString().slice(0, 10)}.csv`,
          rows: csvRows,
        }}
      />

      {rows.length > 0 && (
        <Alert color="gray" variant="light" icon={<IconInfoCircle size={14} />}>
          <Text size="xs">
            Click any row to open the session list for that bot — each row drill-through only shows
            sessions that matched the early-abandon criteria, not all sessions for the bot.
          </Text>
        </Alert>
      )}

      <SessionListModal
        opened={!!drill}
        onClose={() => setDrill(null)}
        title={drill ? `${drill.bot.replace(/^PROD-/, '')} — early-abandon sessions` : ''}
        subtitle={drill ? `${drill.sessions.length} session${drill.sessions.length === 1 ? '' : 's'} matched` : ''}
        sessions={drill?.sessions ?? []}
        onViewSession={onViewSession}
        window={window}
      />
    </Stack>
  );
}

// Build per-bot rows + per-bot session lists keyed by canonical bot name.
// Also returns the set of early-abandon sessions grouped by bot so the
// drill-through modal can show only matching rows (not all sessions).
function buildRows(sessions, agentIdToName) {
  const resolveBotName = (s) =>
    s.agentName || (s.agentId && agentIdToName[s.agentId]) || '(unnamed)';

  // Per-bot running stats.
  const perBot = new Map();
  const sessionsByBot = new Map();

  for (const s of sessions) {
    const bot = resolveBotName(s);
    if (!perBot.has(bot)) {
      perBot.set(bot, {
        bot,
        totalSessions: 0,
        earlyCount: 0,
        buckets: { d0_5: 0, d5_10: 0, d10_20: 0, d20_30: 0 },
        _sumMessages: 0,
        _msgCount: 0,
        _sumDurationMs: 0,
        _durationCount: 0,
      });
    }
    const row = perBot.get(bot);
    row.totalSessions += 1;

    const duration = Number(s.duration);
    const messages = s.noOfTotalMessages == null ? null : Number(s.noOfTotalMessages);
    const durationHit = Number.isFinite(duration) && duration < EARLY_DURATION_MS;
    const messageHit = messages == null || messages < EARLY_MESSAGE_FLOOR;
    const isEarly = durationHit || messageHit;

    if (!isEarly) continue;

    row.earlyCount += 1;
    if (!sessionsByBot.has(bot)) sessionsByBot.set(bot, []);
    sessionsByBot.get(bot).push(s);

    if (messages != null) {
      row._sumMessages += messages;
      row._msgCount += 1;
    }
    if (Number.isFinite(duration)) {
      row._sumDurationMs += duration;
      row._durationCount += 1;
      // Bucket on duration regardless of why it was flagged. Sessions
      // flagged only via message-count with no finite duration don't go
      // into a bucket — surfaced via the earlyCount vs. bucket-sum delta.
      for (const b of BUCKETS) {
        if (duration >= b.lo && duration < b.hi) {
          row.buckets[b.id] += 1;
          break;
        }
      }
    }
  }

  const rows = [...perBot.values()]
    .filter((r) => r.earlyCount > 0)
    .map((r) => ({
      key: r.bot,
      ...r,
      avgMessages: r._msgCount > 0 ? r._sumMessages / r._msgCount : null,
      avgDurationMs: r._durationCount > 0 ? r._sumDurationMs / r._durationCount : null,
    }))
    .sort((a, b) => b.earlyCount - a.earlyCount);

  const totalEarly = rows.reduce((n, r) => n + r.earlyCount, 0);
  return { rows, totalEarly, totalSessions: sessions.length, sessionsByBot };
}
